/**
 * 📥 Import Mode Modal
 * Asks the admin, before picking a file, whether this import should also
 * create login accounts (email/phone + password) or only bring in
 * processable employee data (attendance/payroll/reporting, no system access).
 */

import React, { useEffect, useState } from "react";
import { X, Users, ShieldCheck, Database, ArrowRight, Hash, Check } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";

const API_URL = process.env.REACT_APP_BASE_URL;

const ImportModeModal = ({
  open,
  importMode,
  setImportMode,
  quotaStatus,
  quotaLoading,
  onConfirm,
  onClose,
  theme,
  language,
  clientName,
}) => {
  const isDark = theme === "dark";
  const isRtl = language === "ar";

  // Superadmin-controlled: when false, the choice between "with accounts"
  // and "processing only" is hidden and every import is forced to
  // "with_accounts", the admin only sees the accounts-import info and the
  // remaining quota.
  //
  // `allowModeChoice` used to default to `true` while quotaStatus was still
  // loading, so both cards rendered as selectable immediately, if the
  // fetch then resolved with the choice disabled, the effect below quietly
  // snapped the selection back to "with_accounts" and the second card
  // disappeared a beat later. That's the visible glitch: both options
  // flash, then one vanishes. `modeChoiceLoading` (first load, no cached
  // status yet) lets the UI show a neutral loading placeholder instead of a
  // selectable-then-reverted choice until we actually know the answer.
  const modeChoiceLoading = quotaLoading && !quotaStatus;
  // Off unless explicitly turned on, null/undefined (no config row, or an
  // older row from before this field existed) means "no".
  const allowModeChoice = quotaStatus?.allowImportModeChoice === true;

  useEffect(() => {
    if (!open) return;
    if (modeChoiceLoading) return;
    if (!allowModeChoice && importMode !== "with_accounts") {
      setImportMode("with_accounts");
    }
  }, [open, modeChoiceLoading, allowModeChoice, importMode, setImportMode]);

  // Read-only Infinity Code format hint for this client, mirrors the one
  // shown on the single Add Employee form, so bulk imports get the same
  // heads-up about what format their "Infinity Code" column should follow.
  const [codeFormatHint, setCodeFormatHint] = useState("");
  useEffect(() => {
    if (!open) return;
    const client = clientName || localStorage.getItem("clientName") || "";
    if (!client) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/configurations/${encodeURIComponent(client)}`);
        const json = await response.json().catch(() => null);
        if (!cancelled && response.ok && json?.data?.codeFormat) {
          setCodeFormatHint(json.data.codeFormat);
        }
      } catch (err) {
        console.warn("Could not load Infinity Code format hint:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientName]);

  if (!open) return null;

  const colors = {
    accent: "#2e7cba",
    accentDark: "#245f92",
    text: isDark ? "#ffffff" : "#0f172a",
    subtext: isDark ? "rgba(255,255,255,0.62)" : "#64748b",
    faint: isDark ? "rgba(255,255,255,0.4)" : "#94a3b8",
    border: isDark ? "rgba(255,255,255,0.1)" : "rgba(148,163,184,0.22)",
    surface: isDark ? "rgba(255,255,255,0.035)" : "rgba(15,23,42,0.02)",
    green: "#10b981",
    amber: "#f59e0b",
    red: "#ef4444",
    gray: isDark ? "rgba(255,255,255,0.5)" : "#94a3b8",
  };

  // Builds the small remaining-quota pill shown on each card. `kind` picks
  // which of the two quotas (employees vs. accounts) is the binding one for
  // that option, "with_accounts" needs an account seat, "processing_only"
  // only ever needs an employee seat.
  const renderQuotaPill = (kind) => {
    const label = kind === "accounts"
      ? (isRtl ? "حسابات" : "accounts")
      : (isRtl ? "موظفون" : "employees");

    if (quotaLoading && !quotaStatus) {
      return (
        <span style={{
          fontSize: "10.5px", fontWeight: 700, padding: "3px 10px", borderRadius: "999px",
          background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
          color: colors.gray, whiteSpace: "nowrap",
        }}>
          {isRtl ? "جارٍ التحقق…" : "Checking…"}
        </span>
      );
    }

    const bucket = quotaStatus?.[kind];
    if (!bucket) return null;

    const { quota, used, remaining } = bucket;
    let text;
    let color;
    if (quota == null || remaining == null) {
      text = isRtl ? "غير محدود" : "Unlimited";
      color = colors.accent;
    } else if (remaining <= 0) {
      text = isRtl ? "لا يوجد متبقٍ" : "0 left";
      color = colors.red;
    } else if (remaining <= 5) {
      text = isRtl ? `${remaining} متبقٍ` : `${remaining} left`;
      color = colors.amber;
    } else {
      text = isRtl ? `${remaining} متبقٍ` : `${remaining} left`;
      color = colors.green;
    }

    return (
      <span
        title={
          quota == null
            ? undefined
            : isRtl
              ? `تم استخدام ${used} من ${quota} ${label}`
              : `${used} of ${quota} ${label} used`
        }
        style={{
          fontSize: "10.5px",
          fontWeight: 700,
          padding: "3px 10px",
          borderRadius: "999px",
          background: `${color}16`,
          border: `1px solid ${color}38`,
          color,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {text}
      </span>
    );
  };

  const allOptions = [
    {
      value: "with_accounts",
      icon: <ShieldCheck size={19} />,
      title: isRtl ? "بحساب دخول" : "With Login Account",
      description: isRtl
        ? "يُنشئ سجل الموظف ويمنحه حساب دخول (بريد إلكتروني/هاتف وكلمة مرور) للوصول الذاتي إلى النظام. يُحتسب من حصتي الموظفين والحسابات معًا."
        : "Creates the employee record and issues a login (email/phone & password) for self-service access. Counts against both the employee and account quotas.",
      quotaKind: "accounts",
    },
    {
      value: "processing_only",
      icon: <Database size={19} />,
      title: isRtl ? "سجل الموظف فقط" : "Employee Record Only",
      description: isRtl
        ? "يُنشئ سجل الموظف لأغراض الحضور والرواتب والتقارير دون إنشاء أي حساب دخول. يُحتسب من حصة الموظفين فقط."
        : "Creates the employee record for attendance, payroll, and reporting. No login is created. Counts only against the employee quota.",
      quotaKind: "employees",
    },
  ];

  // When the superadmin has disabled the choice, the "processing only"
  // option is not just hidden, it is never referenced, named, or
  // rendered as an alternative anywhere in this modal. The non-choice view
  // below is its own plain informational panel (see the render below),
  // deliberately not built from `allOptions`, so there's nothing in the
  // markup that could leak the other mode's title/description into a
  // "you could also..." hint.
  const options = allowModeChoice ? allOptions : [];

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3000,
          background: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
          animation: "importModeFadeIn 200ms ease-out",
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 3001,
          animation: "importModeSlideUp 260ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          width: "min(500px, calc(100vw - 32px))",
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          background: isDark
            ? "linear-gradient(160deg, #0f172a 0%, #16223a 100%)"
            : "linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)",
          borderRadius: 20,
          boxShadow: isDark
            ? "0 30px 70px rgba(0,0,0,0.75), 0 0 1px rgba(255,255,255,0.08)"
            : "0 24px 55px rgba(15,23,42,0.16), 0 0 1px rgba(15,23,42,0.04)",
          border: `1px solid ${colors.border}`,
        }}
        onClick={(e) => e.stopPropagation()}
        dir={isRtl ? "rtl" : "ltr"}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
          padding: "24px 24px 20px",
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: "13px",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
              boxShadow: `0 8px 18px ${isDark ? "rgba(46,124,186,0.35)" : "rgba(46,124,186,0.28)"}`,
            }}>
              <Users size={21} color="#ffffff" />
            </div>
            <div>
              <h2 style={{ margin: 0, marginBottom: "5px", fontSize: "18.5px", fontWeight: 800, color: colors.text, letterSpacing: "-0.01em" }}>
                {isRtl ? "استيراد الموظفين" : "Import Employees"}
              </h2>
              <p style={{ margin: 0, fontSize: "13px", color: colors.subtext, fontWeight: 500, lineHeight: 1.5 }}>
                {allowModeChoice
                  ? (isRtl
                      ? "اختر طريقة إضافة هؤلاء الموظفين قبل تحديد الملف."
                      : "Choose how these employees should be added before selecting a file.")
                  : (isRtl
                      ? "حدد ملف الإكسل للمتابعة."
                      : "Select your Excel file to continue.")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.05)",
              border: "none",
              borderRadius: "10px",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: colors.subtext,
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ borderTop: `1px solid ${colors.border}` }} />

        {/* Options. When the superadmin has enabled the choice, this is a
            selectable chooser between both modes. When it's disabled, the
            other mode is not merely hidden, nothing below is built from
            `allOptions`/`opt.title`/`opt.description` at all, so there is no
            code path that could leak its name or description into this
            view. It's a plain, self-contained statement of what importing
            here does, full stop. While we don't yet know which view this
            will resolve to, show one neutral loading placeholder instead,
            this is what prevents the "both cards flash, then one
            disappears" glitch: we never render a selectable state we might
            have to take back a moment later. */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {modeChoiceLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "16px 18px",
                borderRadius: "14px",
                border: `1.5px solid ${colors.border}`,
                background: colors.surface,
                color: colors.subtext,
                fontSize: "13px",
                animation: "importModePulse 1.4s ease-in-out infinite",
              }}
            >
              {isRtl ? "جارٍ التحقق…" : "Checking…"}
            </div>
          ) : !allowModeChoice ? (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "14px",
                padding: "16px 18px",
                borderRadius: "14px",
                background: isDark ? "rgba(46,124,186,0.1)" : "rgba(46,124,186,0.055)",
                border: `1px solid ${isDark ? "rgba(46,124,186,0.22)" : "rgba(46,124,186,0.16)"}`,
                borderInlineStart: `3px solid ${colors.accent}`,
              }}
            >
              <div style={{
                width: 38,
                height: 38,
                borderRadius: "11px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: colors.accent,
                color: "#ffffff",
              }}>
                <ShieldCheck size={19} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: "10.5px",
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: colors.accent,
                  marginBottom: "5px",
                }}>
                  {isRtl ? "إنشاء الحسابات" : "Account Creation"}
                </div>
                <div style={{ fontSize: "13px", color: colors.text, lineHeight: 1.55, fontWeight: 500 }}>
                  {isRtl
                    ? "سيحصل كل موظف يتم استيراده على حساب دخول تلقائيًا (بريد إلكتروني أو رقم هاتف مع كلمة مرور) لتمكينه من الوصول إلى النظام فورًا."
                    : "Every employee you import automatically receives a system login (an email or phone number paired with a password) for immediate access."}
                </div>
                <div style={{ marginTop: "10px" }}>
                  {renderQuotaPill("accounts")}
                </div>
              </div>
            </div>
          ) : options.map((opt) => {
            const selected = importMode === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setImportMode(opt.value)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "13px",
                  textAlign: isRtl ? "right" : "left",
                  padding: "15px 17px",
                  borderRadius: "14px",
                  cursor: "pointer",
                  border: selected ? `1.5px solid ${colors.accent}` : `1.5px solid ${colors.border}`,
                  background: selected
                    ? (isDark ? "rgba(46,124,186,0.14)" : "rgba(46,124,186,0.06)")
                    : "transparent",
                  boxShadow: selected
                    ? `0 4px 14px ${isDark ? "rgba(46,124,186,0.22)" : "rgba(46,124,186,0.14)"}`
                    : "none",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{
                  width: 38,
                  height: 38,
                  borderRadius: "11px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: selected ? colors.accent : (isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"),
                  color: selected ? "#ffffff" : colors.subtext,
                  transition: "all 0.15s ease",
                }}>
                  {opt.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "4px" }}>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: selected ? colors.accent : colors.text }}>
                      {opt.title}
                    </span>
                    {renderQuotaPill(opt.quotaKind)}
                  </div>
                  <div style={{ fontSize: "12.5px", color: colors.subtext, lineHeight: 1.55 }}>
                    {opt.description}
                  </div>
                </div>
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  border: `1.5px solid ${selected ? colors.accent : colors.border}`,
                  background: selected ? colors.accent : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: "1px",
                  transition: "all 0.15s ease",
                }}>
                  {selected && <Check size={12} color="#ffffff" strokeWidth={3} />}
                </div>
              </button>
            );
          })}

          {/* Code format hint */}
          {codeFormatHint && (
            <div style={{
              display: "flex", alignItems: "center", gap: "8px",
              marginTop: "4px", padding: "10px 14px", borderRadius: "11px",
              background: isDark ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.06)",
              border: `1px solid ${isDark ? "rgba(139,92,246,0.25)" : "rgba(139,92,246,0.2)"}`,
            }}>
              <Hash size={14} color="#8b5cf6" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: "12px", color: isDark ? "rgba(255,255,255,0.75)" : "#475569" }}>
                {isRtl
                  ? `صيغة هذا العميل: ${codeFormatHint}`
                  : `This client's format: ${codeFormatHint}`}
              </span>
            </div>
          )}
        </div>

        <div style={{ borderTop: `1px solid ${colors.border}` }} />

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "18px 24px" }}>
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              border: `1px solid ${colors.border}`,
              background: "transparent",
              color: colors.subtext,
              fontSize: "13.5px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {isRtl ? "إلغاء" : "Cancel"}
          </button>
          <button
            onClick={onConfirm}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "10px 22px",
              borderRadius: "10px",
              border: "none",
              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
              color: "#ffffff",
              fontSize: "13.5px",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: `0 6px 16px ${isDark ? "rgba(46,124,186,0.35)" : "rgba(46,124,186,0.3)"}`,
              transition: "all 0.15s ease",
            }}
          >
            {isRtl ? "متابعة واختيار الملف" : "Continue to File"}
            <ArrowRight size={14} style={{ transform: isRtl ? "rotate(180deg)" : "none" }} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes importModeFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes importModeSlideUp {
          from { opacity: 0; transform: translate(-50%, -50%) translateY(20px); }
          to { opacity: 1; transform: translate(-50%, -50%) translateY(0); }
        }
        @keyframes importModePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
    </>
  );
};

export default ImportModeModal;
